import { Injectable } from '@angular/core';
import { environment } from 'src/environments/environment';
import { isK3000domain } from 'src/app/core/common';
import { LocalStorage } from '../../auth/local.storage';
import { CloudAuthBaseService, AuthModel, IBasePopWinAuth, CLOUD_PKCE_VERIFIER_KEY } from '../cloud-auth-base.service';
import { RedirectCloudUrl } from '../enums/cloud-service.enum';
import { HttpClient } from '@angular/common/http';
import { AccountService } from '../../auth/account.service';

export interface IOneDriveUserPorfile {
  businessPhones: any[];
  displayName: string | null;
  givenName: string | null;
  id: string | null;
  jobTitle: string | null;
  mail: string | null;
  mobilePhone: string | null;
  officeLocation: string | null;
  preferredLanguage: string | null;
  surname: string | null;
  userPrincipalName: string | null;
}

@Injectable({
  providedIn: 'root'
})
export class OneDriveAuthService extends CloudAuthBaseService {
  private ONEDRIVE_INFO_KEY = 'ONEDRIVE_INFO_KEY';
  private authUrl: string = 'https://login.microsoftonline.com/common/oauth2/v2.0';

  private get redirectUri(): string {
    return encodeURI(`${window.location.origin}/cloud-redirect/${RedirectCloudUrl.OneDrive}`);
  }

  private get redirectLogoutUri(): string {
    return encodeURI(`${window.location.origin}/cloud-logout/${RedirectCloudUrl.OneDrive}`);
  }

  private get clientId(): string {
    return isK3000domain() ? environment.kurzweil.oneDrive.clientId :
      environment.alinea.oneDrive.clientId;
  }

  private get scope(): string {
    const scope = isK3000domain() ? environment.kurzweil.oneDrive.scope :
      environment.alinea.oneDrive.scope;

    return encodeURI(scope.join(' '));
  }

  public get oneDriveInfo(): OneDriveAuthInfo | null {
    return this.localStorage.getWithExpiry<OneDriveAuthInfo>(this.getKey());
  }

  constructor(
    protected readonly localStorage: LocalStorage,
    private http: HttpClient,
    private accountService: AccountService
  ) {
    super(localStorage);
  }

  public async authorizeOneDrive(): Promise<void> {
    this.logout();

    const { verifier, challenge, method } = this.generatePkce();
    this.localStorage.set(CLOUD_PKCE_VERIFIER_KEY, verifier);

    const options: AuthModel = {
      localStorageKey: this.getKey(),
      url: `${this.authUrl}/authorize?client_id=${this.clientId}` +
           `&response_type=code` +
           `&redirect_uri=${this.redirectUri}` +
           `&scope=${this.scope}` +
           `&prompt=select_account` +
           `&code_challenge=${challenge}` +
           `&code_challenge_method=${method}`,
      windowName: 'OneDriveAuthPopup'
    };

    await super.authorizePopup(options);
  }

  public logout(): void {
    this.localStorage.remove(this.getKey());
  }

  public isConnected() {
    let authInfo = this.oneDriveInfo;
    if (authInfo) {
      return true;
    }
    return false;
  }

  public unAttachDrive() {
    this.logout();
    const opts: IBasePopWinAuth = {
      url: `https://login.live.com/oauth20_logout.srf?client_id=${this.clientId}&redirect_uri=${this.redirectLogoutUri}`,
      windowName: 'OneDriveAuthPopup',
    }
    this.logoutPopup(opts);
  }

  async getUserInfo(): Promise<IOneDriveUserPorfile | null> {
    return new Promise((res) => {
      let url = 'https://graph.microsoft.com/v1.0/me';
      const authData = this.oneDriveInfo;
      if (authData) {
        this.http.get(url, {
          headers: {
            Authorization: 'Bearer ' + authData.access_token
          }
        }).toPromise()
          .then((e: IOneDriveUserPorfile) => {
            res(e);
          }).catch(() => {
            res(null);
          })
      } else {
        res(null);
      }
    });
  }

  private getKey(): string {
    const key = this.ONEDRIVE_INFO_KEY + this.accountService.userId;

    // check if there is still some info left on the 'old' key
    const info = this.localStorage.getWithExpiry<OneDriveAuthInfo>(this.ONEDRIVE_INFO_KEY);

    // if there is, remove it and save the info under the 'new' key
    if (info) {
      this.localStorage.remove(this.ONEDRIVE_INFO_KEY);
      const driveInfo = info as OneDriveAuthInfo;
      this.localStorage.setWithExpiry<OneDriveAuthInfo>(key, driveInfo, driveInfo.expires_in * 1000);
    }

    // return the key
    return key;
  }
}

export interface OneDriveAuthInfo {
  token_type: string;
  expires_in: number;
  scope: string;
  access_token: string;
  refresh_token?: string; // available with auth code + PKCE flow
}
